import {
  createContext,
  useContext,
  useMemo,
  useState,
  ReactNode,
  useRef,
  useCallback,
} from "react";
import {
  DEFAULT_TOAST,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../pages/LandingPage/constants";
import {
  ActiveAccountsReceivableRecordState,
  PrimitiveValueType,
  UIStateContextProps,
} from "../types";

const UIStateContext = createContext<UIStateContextProps | undefined>(
  undefined,
);

export const useUIStateContext = (): UIStateContextProps => {
  const context = useContext(UIStateContext);
  if (!context) {
    throw new Error("useUIStateContext must be used within a UIStateProvider");
  }
  return context;
};

interface UIStateProviderProps {
  children: ReactNode;
}

export const UIStateProvider = ({
  children,
}: UIStateProviderProps): JSX.Element => {
  const [networkErrorToast, setNetworkErrorToast] = useState(DEFAULT_TOAST);
  const [landingPageToast, setLandingPageToast] = useState(DEFAULT_TOAST);
  const [recordPageToast, setRecordPageToast] = useState(DEFAULT_TOAST);
  const [isRecordOpen, setIsRecordOpen] = useState(false);
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);
  const [isRecordDeleteDialogOpen, setIsRecordDeleteDialogOpen] =
    useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isFileUploadDialogOpen, setIsFileUploadDialogOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [pageSizeOption, setPageSizeOption] = useState(
    DEFAULT_ROWS_PER_PAGE_OPTIONS[2],
  );
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [selectedTabSectionKey, setSelectedTabSectionKey] = useState<
    string | undefined
  >(undefined);
  const [selectedDashboardTabSectionKey, setSelectedDashboardTabSectionKey] =
    useState<string | undefined>(undefined);

  const saveRequestIdRef = useRef(0);
  const [saveRequestId, setSaveRequestId] = useState(0);
  const convertARScheduleTypeIdRef = useRef(0);
  const [convertARScheduleTypeId, setConvertARScheduleTypeId] = useState(0);

  const [chipFilterValue, setChipFilterValue] = useState<PrimitiveValueType[]>(
    [],
  );

  const [
    activeAccountsReceivableRecordMigrationInfo,
    setActiveAccountsReceivableRecordMigrationInfo,
  ] = useState<ActiveAccountsReceivableRecordState>({
    gridTableToast: DEFAULT_TOAST,
    isMigrationDialogOpen: false,
    newARScheduleCode: null,
  });

  const incrementSaveRequestId = useCallback(() => {
    saveRequestIdRef.current += 1;
    setSaveRequestId(saveRequestIdRef.current);
  }, []);

  const resetSaveRequestId = useCallback(() => {
    saveRequestIdRef.current = 0;
    setSaveRequestId(0);
  }, []);

  const incrementConvertARScheduleTypeId = useCallback(() => {
    convertARScheduleTypeIdRef.current += 1;
    setConvertARScheduleTypeId(convertARScheduleTypeIdRef.current);
  }, []);

  const resetConvertARScheduleTypeId = useCallback(() => {
    convertARScheduleTypeIdRef.current = 0;
    setConvertARScheduleTypeId(0);
  }, []);

  const value = useMemo(
    () => ({
      networkErrorToast,
      setNetworkErrorToast,
      landingPageToast,
      setLandingPageToast,
      recordPageToast,
      setRecordPageToast,
      isRecordOpen,
      setIsRecordOpen,
      isSideSheetOpen,
      setIsSideSheetOpen,
      isFileUploadDialogOpen,
      setIsFileUploadDialogOpen,
      isRecordDeleteDialogOpen,
      setIsRecordDeleteDialogOpen,
      isExportDialogOpen,
      setIsExportDialogOpen,
      activeTabIndex,
      setActiveTabIndex,
      selectedTabSectionKey,
      setSelectedTabSectionKey,
      selectedDashboardTabSectionKey,
      setSelectedDashboardTabSectionKey,
      searchValue,
      setSearchValue,
      currentPage,
      setCurrentPage,
      pageSizeOption,
      setPageSizeOption,
      saveRequestId,
      incrementSaveRequestId,
      resetSaveRequestId,
      convertARScheduleTypeId,
      incrementConvertARScheduleTypeId,
      resetConvertARScheduleTypeId,
      chipFilterValue,
      setChipFilterValue,
      activeAccountsReceivableRecordMigrationInfo,
      setActiveAccountsReceivableRecordMigrationInfo,
    }),
    [
      networkErrorToast,
      landingPageToast,
      recordPageToast,
      isRecordOpen,
      isSideSheetOpen,
      isRecordDeleteDialogOpen,
      isExportDialogOpen,
      isFileUploadDialogOpen,
      activeAccountsReceivableRecordMigrationInfo,
      currentPage,
      pageSizeOption,
      setPageSizeOption,
      searchValue,
      activeTabIndex,
      selectedTabSectionKey,
      selectedDashboardTabSectionKey,
      saveRequestId,
      chipFilterValue,
      setChipFilterValue,
      pageSizeOption,
      setPageSizeOption,
      convertARScheduleTypeId,
      incrementConvertARScheduleTypeId,
      resetConvertARScheduleTypeId,
    ],
  );

  return (
    <UIStateContext.Provider value={value}>{children}</UIStateContext.Provider>
  );
};
